import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  AppBar, Badge, Box, Button, Divider, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TpmsDashboard from './TpmsDashboard';
import TpmsVehiclePage from './TpmsVehiclePage';
import TpmsAlertsPage from './TpmsAlertsPage';
import { tpms } from './api';
import { sessionActions } from '../store';
import brand from '../shell/brand';

// The whole application, for somebody whose job is tyres.
//
// Mounted instead of the operations shell, not inside it. That is the point: the operations shell
// loads the plant bootstrap and polls positions, and both are refused to this role - so hanging a
// tyre page off it would give a user a broken application and a stream of denied requests. Here
// there is no map, no trip, no navigation to one, and nothing that asks for a position.
const WIDTH = 216;

const TpmsShell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const [leaving, setLeaving] = useState(false);
  const [openAlerts, setOpenAlerts] = useState(0);

  const vehicleId = location.pathname.match(/\/tpms\/vehicles\/(\d+)/)?.[1];
  const tab = location.pathname.startsWith('/tpms/alerts') ? 'alerts' : 'fleet';

  // The count beside Alerts, so somebody on the fleet page knows there is something to look at
  // without going to look. The only thing this shell asks for on its own behalf.
  useEffect(() => {
    const controller = new AbortController();
    const load = () => tpms('/api/tpms/alerts', 'GET', null, controller.signal)
      .then((rows) => setOpenAlerts(Array.isArray(rows) ? rows.length : 0))
      .catch(() => {});
    load();
    const timer = setInterval(load, 30000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [location.pathname]);

  const signOut = async () => {
    setLeaving(true);
    try { await fetch('/api/session', { method: 'DELETE' }); } catch { /* leaving anyway */ }
    dispatch(sessionActions.updateUser(null));
    window.location.replace('/login');
  };

  const items = [
    {
      key: 'fleet', label: 'Fleet', icon: <DonutLargeIcon />, to: '/tpms', badge: 0,
    },
    {
      key: 'alerts', label: 'Alerts', icon: <WarningAmberIcon />, to: '/tpms/alerts', badge: openAlerts,
    },
  ];

  const page = () => {
    if (vehicleId) return <TpmsVehiclePage vehicleId={vehicleId} />;
    return tab === 'alerts' ? <TpmsAlertsPage /> : <TpmsDashboard />;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex' }}>
      {/* A rail on a desktop, where there is room for it beside the table. On a phone - which is
          what somebody standing at a lorry is holding - the same destinations sit along the bottom
          where a thumb reaches, rather than behind a button that has to be opened first. */}
      {wide && (
        <Drawer
          variant="permanent"
          sx={{
            width: WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: WIDTH, boxSizing: 'border-box', borderRight: 1, borderColor: 'divider',
            },
          }}
        >
          <Toolbar sx={{ px: 2 }}>
            <Typography variant="h6" fontWeight={700} noWrap>{`${brand.name} tyres`}</Typography>
          </Toolbar>
          <Divider />
          <List sx={{ py: 1 }}>
            {items.map((item) => (
              <ListItemButton
                key={item.key}
                selected={tab === item.key}
                onClick={() => navigate(item.to)}
                sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Badge badgeContent={item.badge} color="error">{item.icon}</Badge>
                </ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: tab === item.key ? 700 : 500 }} />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Button fullWidth onClick={signOut} disabled={leaving}>Sign out</Button>
          </Box>
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0, pb: wide ? 0 : 8 }}>
        {!wide && (
          <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Toolbar sx={{ gap: 2 }}>
              <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }} noWrap>{`${brand.name} tyres`}</Typography>
              <Button onClick={signOut} disabled={leaving}>Sign out</Button>
            </Toolbar>
          </AppBar>
        )}

        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
          {/* Chosen from the path rather than by a nested <Routes>.
              This shell is returned early from App, which is itself an element inside the
              application's route tree, so a nested router here matches against a path segment that
              tree has already consumed - and absolute paths like "/tpms" never match. The shell
              rendered, the body did not, and nothing threw: a blank page with working tabs.
              Reading the path directly cannot be wrong about where it is. */}
          {page()}
        </Box>
      </Box>

      {!wide && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          pb: 'env(safe-area-inset-bottom, 0px)',
          zIndex: (t) => t.zIndex.appBar,
        }}
        >
          {items.map((item) => (
            <Button
              key={item.key}
              fullWidth
              onClick={() => navigate(item.to)}
              sx={{ py: 1.25, borderRadius: 0, fontWeight: tab === item.key ? 700 : 500 }}
              startIcon={<Badge badgeContent={item.badge} color="error">{item.icon}</Badge>}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};

export { tpms };
export default TpmsShell;
